"use client";

import { MessageSquare, Image as ImageIcon } from "lucide-react";

import { Badge } from "@/app/_module/components/ui/badge";
import type { SMSRecord } from "@/app/_module/types/communication";
import { formatRelativeTime } from "@/app/_module/utils/formatters";
import { useSmsConversation } from "@/app/_module/contexts/SmsConversationContext";
import { getSmsFailureReason } from "@/app/_module/utils/smsFailureReason";
import { ViewLeadButton } from "./ViewLeadButton";

interface SMSListProps {
  smsRecords: SMSRecord[];
}

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "Unknown";
  const cleaned = phone.replace(/\D/g, '');
  // Remove leading 1 if present (US country code)
  const digits = cleaned.startsWith('1') && cleaned.length === 11 ? cleaned.slice(1) : cleaned;
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone; // Return original if not a 10-digit number
};

export const SMSList = function({ smsRecords }: SMSListProps) {
  const { startConversation } = useSmsConversation();

  // Handle clicking on an SMS record to open the chat widget
  const handleSmsClick = (sms: SMSRecord) => {
    startConversation({
      leadId: sms.leadId || '',
      leadName: sms.contactName || formatPhoneNumber(sms.phoneNumber),
      phoneNumber: sms.phoneNumber,
    });
  };
  const getStatusBadge = (status: SMSRecord["status"]) => {
    const statusConfig = {
      sent: {
        variant: "default" as const,
        label: "Sent",
        className: "bg-blue-100 text-blue-800 border-blue-200"
      },
      delivered: {
        variant: "default" as const,
        label: "Delivered",
        className: "bg-green-100 text-green-800 border-green-200"
      },
      received: {
        variant: "default" as const,
        label: "Received",
        className: "bg-indigo-100 text-indigo-800 border-indigo-200"
      },
      failed: {
        variant: "destructive" as const,
        label: "Failed",
        className: "bg-red-100 text-red-800 border-red-200"
      },
      pending: {
        variant: "secondary" as const,
        label: "Pending",
        className: "bg-yellow-100 text-yellow-800 border-yellow-200"
      },
    } as const;

    const config = statusConfig[status] || {
      variant: "secondary" as const,
      label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown',
      className: ""
    };

    return (
      <Badge className={`text-xs ${config.className}`} variant={config.variant}>
        {config.label}
      </Badge>
    );
  };

  // Truncate message for preview (first 100 characters)
  const getMessagePreview = (sms: SMSRecord, maxLength: number = 100): string => {
    const message = sms.message;
    const hasMms = sms.mediaUrls && sms.mediaUrls.length > 0;
    const isMmsOnly = message === '[MMS Attachment]' || (!message && hasMms);

    if (isMmsOnly) {
      return "📷 Photo";
    }

    if (hasMms && message && message !== '[MMS Attachment]') {
      // MMS with text
      const preview = message.length <= maxLength - 8 ? message : message.substring(0, maxLength - 11) + "...";
      return `📷 ${preview}`;
    }

    if (!message) return "";
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + "...";
  };

  // Check if message is MMS (has media)
  const isMmsMessage = (sms: SMSRecord): boolean => {
    return (sms.mediaUrls && sms.mediaUrls.length > 0) || sms.message === '[MMS Attachment]';
  };

  if (smsRecords.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No SMS messages found.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {smsRecords.map((sms) => (
        <div
          key={sms.id}
          onClick={() => handleSmsClick(sms)}
          className="flex items-start justify-between p-4 bg-card rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer"
        >
          {/* role="button" is on the content, not the card, so View Lead
              isn't a button nested inside another button. */}
          <div
            className="flex items-start space-x-3 flex-1 min-w-0"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSmsClick(sms);
              }
            }}
          >
            {isMmsMessage(sms) ? (
              <ImageIcon
                className={`h-4 w-4 mt-1 ${
                  sms.direction === "inbound" ? "text-blue-600" : "text-green-600"
                }`}
              />
            ) : (
              <MessageSquare
                className={`h-4 w-4 mt-1 ${
                  sms.direction === "inbound" ? "text-blue-600" : "text-green-600"
                }`}
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <p className="text-sm font-medium text-foreground truncate">
                  {sms.contactName || formatPhoneNumber(sms.phoneNumber)}
                </p>
                {getStatusBadge(sms.status)}
                {sms.status === "failed" && (() => {
                  const reason = getSmsFailureReason(sms.errorCode, sms.errorMessage);
                  return reason ? (
                    <span
                      className="text-xs text-red-600 truncate"
                      title={reason.hint}
                    >
                      {reason.label}
                    </span>
                  ) : null;
                })()}
              </div>
              <div className="flex items-center space-x-2 mb-2">
                {/* Show phone number if there's a contact name, otherwise don't repeat it */}
                {sms.contactName && (
                  <>
                    <p className="text-xs text-muted-foreground">
                      {formatPhoneNumber(sms.phoneNumber)}
                    </p>
                    <span className="text-xs text-muted-foreground">•</span>
                  </>
                )}
                <p className="text-xs text-muted-foreground">
                  {sms.direction === "inbound" ? "Received" : "Sent"}
                </p>
                {!isMmsMessage(sms) && (
                  <>
                    <span className="text-xs text-muted-foreground">•</span>
                    <p className="text-xs text-muted-foreground">
                      {sms.message?.length || 0} characters
                    </p>
                  </>
                )}
                {isMmsMessage(sms) && (
                  <>
                    <span className="text-xs text-muted-foreground">•</span>
                    <p className="text-xs text-muted-foreground">
                      Photo
                    </p>
                  </>
                )}
              </div>
              <p className="text-sm text-foreground break-words line-clamp-3">
                {getMessagePreview(sms, 150)}
              </p>
            </div>
          </div>
          <div className="text-right ml-4 flex flex-col items-end gap-2">
            <p className="text-xs text-muted-foreground">
              {formatRelativeTime(sms.timestamp)}
            </p>
            {sms.leadId && (
              <ViewLeadButton
                leadId={sms.leadId}
                contactName={sms.contactName || formatPhoneNumber(sms.phoneNumber)}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
